import { CompanyDetails, FormCompanyDetails } from "components/userDetails/interfaces/companyDetails";
import MobxReactRouter from "mobx-react-router";
import React from "react";
import { ClaimStatusEnum, ReleaseRequestStatus, UserStatus } from "shared/constants/enums";
import { ExpiredLinkErrors } from "shared/constants/errorsCodes";
import ElementWidthControl from "shared/interfaces/elementWidthControl";
import { RequestError } from "shared/interfaces/errors/RequestError";
import { PhoneNumberWidthControl } from "shared/interfaces/inputs/phoneNumber/widthControl";
import ButtonProgressSizeControl from "shared/interfaces/buttons/buttonProgress/buttonProgress";
import { AvatarSize } from "shared/interfaces/avatar/avatar";
import routeNames from "shared/routes/constants/routeNames";
import theme, { appAdditionalPalettes, applicationPalettes } from "shared/theme/defaultTheme";
import ElementPaddingControl from "shared/interfaces/elementPaddingControl";
import { FilePreviewStyleProps } from "shared/interfaces/container/filePreview/filePreview";
import { DialogSizeControl } from "shared/interfaces/container/dialog/dialog";
import { Tenancy } from "shared/interfaces/tenancy";
import { escapeRegExp } from "lodash";

export const getTextFieldWidthsBySize = (size: string): ElementWidthControl => {
    if (size === "auto") return { width: "100%", maxWidth: "360px", minWidth: "168px" };
    if (size === "small") return { width: "168px", maxWidth: "168px", minWidth: "168px" };
    if (size === "extra-small") return { width: "100%", maxWidth: "168px", minWidth: "98px" };
    if (size === "tiny") return { width: "100%", maxWidth: "auto", minWidth: "auto" };
    return { width: "360px", maxWidth: "360px", minWidth: "360px" };
};

export const getTextFieldPaddingBySize = (size: string): ElementPaddingControl => {
    if (size === "extra-small") return { padding: theme.spacing(1, 1.25), height: "16px" };
    if (size === "tiny") return { padding: theme.spacing(0.5, 1), height: "24px" };
    return { padding: theme.spacing(1, 2), height: "32px" };
};

export const getContainerWidthsBySize = (size: string): ElementWidthControl => {
    if (size === "full") return { width: "100%", maxWidth: "100%", minWidth: "100%" };
    if (size === "auto") return { width: "100%", maxWidth: "752px", minWidth: "168px" };
    if (size === "small") return { width: "168px", maxWidth: "168px", minWidth: "168px" };
    if (size === "medium") return { width: "460px", maxWidth: "460px", minWidth: "460px" };
    if (size === "big") return { width: "752px", maxWidth: "752px", minWidth: "752px" };
    return { width: "460px", maxWidth: "460px", minWidth: "460px" };
};

export const getPhoneNumberWidthsBySize = (size: string): PhoneNumberWidthControl => {
    if (size === "auto") return { width: "100%", maxWidth: "360px", minWidth: "168px" };
    return { width: "360px", maxWidth: "360px", minWidth: "360px" };
};

export const getPointLineWidthsBySize = (size: string): ElementWidthControl => {
    if (size === "auto") return { width: "100%", maxWidth: "100%", minWidth: "168px" };
    if (size === "small") return { width: "168px", maxWidth: "168px", minWidth: "168px" };
    return { width: "300px", maxWidth: "300px", minWidth: "300px" };
};

export const getButtonSpinnerDimensionsBySize = (size: string): ButtonProgressSizeControl => {
    if (size === "l") return { width: "2rem", height: "2rem", margin: "-1rem 0 0 -1rem" };
    if (size === "m") return { width: "1.75rem", height: "1.75rem", margin: "-0.875rem 0 0 -0.875rem" };
    if (size === "s") return { width: "1.5rem", height: "1.5rem", margin: "-0.725rem 0 0 -0.725rem" };

    return { width: "1.75rem", height: "1.75rem", margin: "-0.875rem 0 0 -0.875rem" };
};

export const getAvatarDimensionsBySize = (size: string): AvatarSize => {
    if (size === "l") return { width: "160px", height: "160px" };
    if (size === "xl") return { width: "80px", height: "80px" };
    if (size === "s") return { width: "64px", height: "64px" };
    if (size === "xs") return { width: "40px", height: "40px" };
    if (size === "xxs") return { width: "24px", height: "24px" };
    return { width: "64px", height: "64px" };
};

export const getFileThumbnailDimensionsBySize = (size: string): AvatarSize => {
    if (size === "s") return { width: "24px", height: "32px" };
    return { width: "48px", height: "64px" };
};

export const getResolutionManagementStatusColors = (statusId?: number): { background: string; color: string } => {
    switch (statusId) {
        case ClaimStatusEnum.ClosedAccepted:
        case ClaimStatusEnum.OpenAccepted:
        case ClaimStatusEnum.ClosedAcceptedPartially:
            return { background: appAdditionalPalettes.success[2], color: applicationPalettes.success[500]! };
        case ClaimStatusEnum.OpenDeclined:
        case ClaimStatusEnum.ClosedDeclined:
            return {
                background: appAdditionalPalettes.error[2],
                color: applicationPalettes.error[500]!,
            };
        case ClaimStatusEnum.Open:
            return {
                background: appAdditionalPalettes.primary[2],
                color: applicationPalettes.primary[500]!,
            };
        default:
            return { background: appAdditionalPalettes.default[2], color: applicationPalettes.default[500]! };
    }
};

export const getReleaseRequestStatusColors = (statusId?: number): { background: string; color: string } => {
    switch (statusId) {
        case ReleaseRequestStatus.Accepted:
            return { background: appAdditionalPalettes.success[2], color: applicationPalettes.success[500]! };
        case ReleaseRequestStatus.Settled:
            return { background: applicationPalettes.success[50]!, color: applicationPalettes.success[600]! };
        case ReleaseRequestStatus.Negotiation:
            return { background: appAdditionalPalettes.warning[1], color: applicationPalettes.warning[500]! };
        case ReleaseRequestStatus.Resolution:
            return { background: appAdditionalPalettes.warning[2], color: applicationPalettes.warning[600]! };
        case ReleaseRequestStatus.Canceled:
            return { background: appAdditionalPalettes.error[1], color: applicationPalettes.error[500]! };
        case ReleaseRequestStatus.Closed:
        case ReleaseRequestStatus.ClosedInternal:
            return { background: appAdditionalPalettes.error[2], color: applicationPalettes.error[600]! };
        case ReleaseRequestStatus.Draft:
        default:
            return { background: appAdditionalPalettes.default[2], color: applicationPalettes.default[500]! };
    }
};

export const getUserStatusColors = (statusId?: number): { background: string; color: string; border: string } => {
    switch (statusId) {
        case UserStatus.Active:
            return {
                background: appAdditionalPalettes.success[2],
                color: applicationPalettes.success["500"]!,
                border: applicationPalettes.success["100"]!,
            };
        case UserStatus.Dormant:
            return {
                background: appAdditionalPalettes.warning[1],
                color: applicationPalettes.warning["500"]!,
                border: applicationPalettes.warning["100"]!,
            };
        case UserStatus.Inactive:
        default:
            return {
                background: appAdditionalPalettes.error[1],
                color: applicationPalettes.error["500"]!,
                border: applicationPalettes.error["100"]!,
            };
    }
};

export const getFilePreviewWidthBySize = (size?: string): FilePreviewStyleProps => {
    if (size === "xl") return { width: "168px", height: "168px" };
    if (size === "l") return { width: "120px", height: "120px" };
    if (size === "m") return { width: "102px", height: "102px" };
    if (size === "s") return { width: "80px", height: "80px" };
    return { width: "80px", height: "80px" };
};

export const getFilePreviewSpinnerSize = (size?: string): number => {
    switch (size) {
        case "xl":
            return 38;
        case "l":
            return 28;
        case "m":
            return 24;
        case "s":
        default:
            return 18;
    }
};

export const getFilePreviewPlayIconSize = (size?: string): number => {
    switch (size) {
        case "xl":
            return 44;
        case "l":
            return 36;
        case "m":
            return 32;
        case "s":
        default:
            return 26;
    }
};

export const getDialogWidthBySize = (size: string): DialogSizeControl => {
    if (size === "large") return { maxWidth: "800px" };
    if (size === "medium") return { maxWidth: "450px" };
    if (size === "small") return { maxWidth: "424px" };
    return { maxWidth: "680px" };
};

export const navigateOnExpiredOrErrorPage = (
    error: RequestError,
    historyObject: MobxReactRouter.RouterStore,
    expiredRoute: string,
    token: string,
    returnUrl: string | null = null
): void => {
    if (error.response?.data?.error_description === ExpiredLinkErrors.TokenIsExpired) {
        const url = returnUrl ? `&returnUrl=${returnUrl}` : "";
        historyObject.push(`${expiredRoute}?token=${token}${url}`);
    } else {
        historyObject.push(routeNames.ERRORS.NOT_FOUND);
    }
};

export const getExpiredOrErrorPageRoute = (error: RequestError, expiredRoute: string, token: string): string => {
    if (error.response?.data?.errors[0]?.code === ExpiredLinkErrors.ChangeEmailRequestIsExpired) {
        return `${expiredRoute}?token=${token}`;
    }

    return routeNames.ERRORS.NOT_FOUND;
};

export const getBoldHighlighted = (str: string, searchStr: string): JSX.Element => {
    const parsedSearchString = escapeRegExp(searchStr);
    const parts = str.split(new RegExp(`(${parsedSearchString})`, "gi"));
    return <>{parts.map(part => (part.toLowerCase() === parsedSearchString.toLowerCase() ? <b>{part}</b> : part))}</>;
};

export const getAdaptedCompanyData = (data: FormCompanyDetails): CompanyDetails => ({
    ...data,
    companyRegistrationNumber: data.companyRegistrationNumber
        ? data.companyRegistrationNumber.companyRegistrationNumber
        : "",
});

export const getFileExtension = (fileName: string): string => {
    const fileNameParts = fileName.split(".");

    return fileNameParts.length ? fileNameParts.pop()! : "";
};

export function getEmptyTenancyFormModel(): Tenancy {
    return {
        startDate: undefined,
        endDate: undefined,
        rent: 0,
        frequencyOfRent: undefined,
        traditionalDepositAmount: 0,
    };
}
